import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { randomUUID } from '@/shared/lib/uuid';

export type PaymentMethod = 'cash' | 'bank_transfer' | 'card';
export type PaymentStatus = 'pending' | 'confirmed' | 'reversed';

export type Payment = {
  id: string;
  contractId: string | null;
  customerId: string | null;
  amount: string;
  currency: string;
  paymentDate: string;
  method: PaymentMethod;
  bankAccountId: string | null;
  status: PaymentStatus;
  reference: string | null;
  notes: string | null;
  reversedAt: string | null;
  reverseReason: string | null;
  receiptPdfUrl: string | null;
  createdAt: string;
  contract?: { id: string; contractNumber: string } | null;
  customer?: { id: string; firstName: string; lastName: string } | null;
  allocations?: {
    id: string;
    paymentScheduleId: string | null;
    amount: string;
    isLateFee: boolean;
    paymentSchedule?: { installmentNumber: number; isDownPayment: boolean } | null;
  }[];
};

export type CreatePaymentInput = {
  contractId: string;
  amount: string;
  paymentDate: string;
  method: PaymentMethod;
  bankAccountId?: string;
  reference?: string;
  notes?: string;
  historical?: boolean;
  overpaymentMode?: 'advance' | 'reduce_principal';
};

export function usePaymentsList(params?: { contractId?: string; customerId?: string }) {
  return useQuery({
    queryKey: ['payments', params],
    queryFn: async () => {
      const res = await api.get<{ data: Payment[]; pagination: { hasMore: boolean } }>('/payments', { params });
      return res.data;
    },
  });
}

export function usePayment(id: string | undefined) {
  return useQuery({
    queryKey: ['payment', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: Payment }>(`/payments/${id}`);
      return res.data.data;
    },
  });
}

export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePaymentInput) => {
      const res = await api.post<{ data: Payment }>('/payments', input, {
        headers: { 'Idempotency-Key': randomUUID() },
      });
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      if (data.contractId) {
        void qc.invalidateQueries({ queryKey: ['contract', data.contractId] });
        void qc.invalidateQueries({ queryKey: ['contract-schedule', data.contractId] });
      }
    },
  });
}

export function useGenerateReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ data: { key: string } }>(`/payments/${id}/regenerate-receipt`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['payment', id] });
    },
  });
}

export function useReversePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await api.post<{ data: Payment }>(`/payments/${id}/reverse`, { reason });
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

// Bank accounts
export type BankAccount = {
  id: string;
  code: string;
  bankName: string;
  accountNumber: string;
  currency: string;
  active: boolean;
};

export function useBankAccounts() {
  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const res = await api.get<{ data: BankAccount[] }>('/bank-accounts');
      return res.data.data;
    },
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; bankName: string; accountNumber: string; currency?: string }) => {
      const res = await api.post<{ data: BankAccount }>('/bank-accounts', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}
